<template>
  <div class="sumo-browse-main">
    <div
      id="app"
      class="vuejs-browse-section"
    >
      <Browse
        :can-search="true"
        :can-sort="true"
        :can-load-more="true"
        :show-results="true"
        :show-browse-header="!collection"
        :is-smart-collection="!!collection"
        :fetch-args="fetchArgs"
        :query-args="queryArgs"
        :group="group"
        :category="category"
        :subcategory="subcategory"
        :maximum-results="collection ? collection.maximum_results : null"
        :full-width="collection ? collection.browse_full_width : false"
      >
        <smart-collection-header v-if="collection" :collection="collection" />
      </Browse>
    </div>
  </div>
</template>
<script>
import { findAttributeOption } from '@/datalayer/helpers/attributes';
import { taxonomyTitle, extractBrowse } from '@/util/global';
import get from 'lodash/get';
import { getTaxonomyBreadcrumbLDSchemaJSONString } from '@/util/schemaLD';
import PrivyMixin from '~/mixins/add-privy';
import WishlistsMixin from '~/mixins/wishlists-mixin';
import BeaconMixin from '~/mixins/beacon';
import Browse from '~/components/browse/Browse';
import SmartCollectionHeader from '~/components/smartcollections/SmartCollectionHeader';

export default {
  key: 'browse',
  components: {
    Browse,
    SmartCollectionHeader,
  },
  mixins: [PrivyMixin, WishlistsMixin, BeaconMixin],
  layout: 'dorado-base',
  async validate({ params, store }) {
    const parts = params.pathMatch.split('/').filter(Boolean);
    const browse = extractBrowse(params.pathMatch);

    if (parts.length === 1) {
      if (['browse', 'search'].includes(parts[0].toLowerCase())) {
        return true;
      }
    }

    if (browse.group) {
      if (!findAttributeOption(store.state.browse.attributes, 'group', browse.group)) {
        const { root_level_collections: rootLevelCollections } = store.state.browse;

        // first item wasn't a valid group then fall check root level smart collections
        if (rootLevelCollections && rootLevelCollections.includes(browse.group)) {
          // if a second level was specified confirm this is actually a dynamic collection
          if (browse.category) {
            const { dynamic_collections: dynamicCollections } = store.state.browse;

            return (dynamicCollections && dynamicCollections.includes(browse.group));
          }
          // only top-level specified so verify its a static collection
          const { static_collections: staticCollections } = store.state.browse;

          return (staticCollections && staticCollections.includes(browse.group));
        }
      }

      if (browse.category) {
        if (!findAttributeOption(store.state.browse.attributes, 'category', browse.category, browse)) {
          return false;
        }

        if (browse.subcategory) {
          if (!findAttributeOption(store.state.browse.attributes, 'subcategory', browse.subcategory, browse)) {
            return false;
          }
        }
      }

      return true;
    } if (browse.collection) {
      if (browse.dynamicCollection) {
        const { dynamic_collections: dynamicCollections } = store.state.browse;

        // /collections/a/b so verify its atleast a valid dynamic collection
        return (dynamicCollections && dynamicCollections.includes(browse.collection));
      }
      const { static_collections: staticCollections } = store.state.browse;

      // only /collections/a so we can verify it exists already
      return (staticCollections && staticCollections.includes(browse.collection));
    }

    return false;
  },
  async asyncData({
    store,
    route,
    error,
    $activity,
    $cachios,
  }) {
    const { query } = route.query;
    const { session: { user = {} } = {} } = store.state;
    const params = extractBrowse(route?.params.pathMatch);
    const data = {
      group: null,
      category: null,
      subcategory: null,
    };

    if (query && user && user.id && $activity) {
      $activity.add({
        actor: `User:${user.id}`,
        verb: 'deal_search',
        full_text: query,
      });
    }

    if (params.group) {
      const group = findAttributeOption(store.state.browse.attributes, 'group', params.group);

      if (group) {
        data.group = group;
      } else {
        // if not a group assume a smart collection - validate checked for us
        params.collection = params.group;
        params.dynamicCollection = params.category;

        // clear the group/category/subcategory params
        params.group = undefined;
        params.category = undefined;
        params.subcategory = undefined;
      }
    }

    if (params.collection) {
      const { handler } = await import(
        /* webpackChunkName: 'datalayer-pages-collectionSlug' */ '@/datalayer/pages/collectionSlug'
      );
      const {
        collection,
        error: err,
        fetchArgs,
      } = await handler(
        true,
        params.dynamicCollection,
        params.collection,
        $cachios,
        store,
      );

      if (collection && !err) {
        return {
          ...data,
          collection,
          fetchArgs,
          queryArgs: route.query,
        };
      }

      return error({ statusCode: 404, message: 'Page not found' });
    }

    if (params.category) {
      data.category = findAttributeOption(store.state.browse.attributes, 'category', params.category);
    }

    if (params.subcategory) {
      data.subcategory = findAttributeOption(store.state.browse.attributes, 'subcategory', params.subcategory);
    }

    return {
      ...data,
      collection: null,
      fetchArgs: null,
      queryArgs: route.query,
    };
  },
  computed: {
    metaDescription() {
      if (this.collection && this.collection.description) {
        const preText = get(this, ['collection', 'subheader'], '');
        if (preText) {
          return `${preText} - ${this.collection.description.replace(/<[^>]+>/g, '')}`;
        }
        return this.collection.description.replace(/<[^>]+>/g, '');
      }
      return get(this, ['collection', 'subheader'], '');
    },
    title() {
      let title = 'Browse software deals for your business.';

      if (this.collection) {
        const ogTitleOverride = this.collection?.open_graph_title;
        const openGraphTitle = this.collection?.open_graph?.title;
        title = ogTitleOverride || openGraphTitle || this.collection?.title;
      } else if (this.group) {
        title = `Shop ${taxonomyTitle((this.subcategory || this.category)?.name, this.group?.name)}`;
      }

      return title;
    },
  },
  head() {
    const meta = [
      {
        hid: 'og:title',
        property: 'og:title',
        content: `${this.title} | AppSumo`,
      },
    ];

    const optionalScripts = [];
    if (this.collection || this.group) {
      optionalScripts.push({
        hid: 'bc-schema',
        type: 'application/ld+json',
        innerHTML: getTaxonomyBreadcrumbLDSchemaJSONString({
          collection: this.collection,
          group: this.group,
          category: this.category,
          subcategory: this.subcategory,
        }),
      });
    }

    const openGraph = this.collection?.open_graph;
    const ogDescriptionOverride = this.collection?.open_graph_description;
    const ogImageOverride = this.collection?.open_graph_image;
    const ogTypeOverride = this.collection?.open_graph_type;

    if (ogDescriptionOverride || openGraph?.description || this.metaDescription) {
      meta.push({
        hid: 'description',
        property: 'description',
        content: ogDescriptionOverride || openGraph?.description || this.metaDescription,
      });
    }

    if (ogDescriptionOverride || openGraph?.description || this.metaDescription) {
      meta.push({
        hid: 'og:description',
        property: 'og:description',
        content: ogDescriptionOverride || openGraph?.description || this.metaDescription,
      });
    }

    if (ogImageOverride || openGraph?.image) {
      meta.push({
        hid: 'og:image',
        property: 'og:image',
        content: ogImageOverride || openGraph?.image,
      });
    }

    meta.push({
      hid: 'og:type',
      property: 'og:type',
      content: ogTypeOverride || openGraph?.type || 'website',
    });

    return {
      title: `${this.title} | AppSumo`,
      meta,
      script: [
        ...optionalScripts,
      ],
      __dangerouslyDisableSanitizersByTagID: {
        'bc-schema': ['innerHTML'],
      },
      bodyAttrs: {
        class: ['sumo-browse-page', 'search-sticky-header'],
      },
    };
  },
};
</script>
